import { createGlobalState } from '@vueuse/core';
import { computed, ref } from 'vue';
import type { Thread } from '~/types/thread.types';
import { randomString } from '~/utils/string';

export type IntelligentCategory = {
    id: string;
    displayName: string;
    description: string;
    classificationHints: string;
    responseHint: string;
    color: string;
    active: boolean;
    sortOrder?: number;
};

export type IntelligentSection = {
    state: string;
    label: string;
    color: string;
    categories: IntelligentCategory[];
};

const SECTION_DEFINITIONS: Readonly<{ state: string; label: string; color: string }[]> = [
    { state: 'needs-action', label: 'Needs Action', color: 'rose' },
    { state: 'follow-up-recommended', label: 'Follow-up Recommended', color: 'amber' },
    { state: 'awaiting-reply', label: 'Awaiting Reply', color: 'sky' },
    { state: 'snoozed', label: 'Snoozed', color: 'slate' },
    { state: 'closed', label: 'Closed', color: 'emerald' },
];

// TODO: Remove prototype data (colors, default categories, sample threads) once the dashboard is wired to the real API.
const CATEGORY_COLORS = {
    sky: '38bdf8',
    rose: 'fb7185',
    amber: 'fbbf24',
    emerald: '34d399',
    slate: '94a3b8',
    violet: 'a78bfa',
} as const;

const createDefaultCategories = (): IntelligentCategory[] => [
    {
        id: randomString(10),
        displayName: 'Tour Request',
        description: '',
        classificationHints:
            'Keywords: tour, visit, schedule a visit, want to see, come in, walk-through. Look for indications of intent to physically visit the center.\n\nMessages from prospects explicitly requesting to schedule a tour of the facility.',
        responseHint: '',
        color: CATEGORY_COLORS.sky,
        active: true,
        sortOrder: 0,
    },
    {
        id: randomString(10),
        displayName: 'Availability Inquiry',
        description: '',
        classificationHints:
            'Keywords: availability, open spots, openings, space, waitlist, full, age group. Look for questions about capacity.\n\nMessages asking about open spots in specific age groups or programs.',
        responseHint: '',
        color: CATEGORY_COLORS.sky,
        active: true,
        sortOrder: 1,
    },
    {
        id: randomString(10),
        displayName: 'Pricing Inquiry',
        description: '',
        classificationHints:
            'Keywords: cost, price, tuition, how much, fees. Focus on direct questions about financial outlay.\n\nMessages asking about the cost of tuition or specific program fees.',
        responseHint: '',
        color: CATEGORY_COLORS.sky,
        active: true,
        sortOrder: 2,
    },
    {
        id: randomString(10),
        displayName: 'General Information Request',
        description: '',
        classificationHints:
            "Keywords: tell me about, programs, curriculum, what do you offer, philosophy, age range. Exclude pricing and availability questions.\n\nMessages asking for general information about TLE's programs, curriculum, or philosophy that doesn't fit into other specific categories.",
        responseHint: '',
        color: CATEGORY_COLORS.sky,
        active: true,
        sortOrder: 3,
    },
    {
        id: randomString(10),
        displayName: 'Service Issue/Complaint',
        description: '',
        classificationHints:
            "Keywords: issue, problem, concern, complaint, unsatisfied, unhappy, difficulty, incident. Look for negative sentiment and specific problems.\n\nMessages from current parents reporting issues, concerns, or complaints about their child's care, a facility issue, or staff interactions.",
        responseHint: '',
        color: CATEGORY_COLORS.rose,
        active: true,
        sortOrder: 4,
    },
    {
        id: randomString(10),
        displayName: 'Re-engagement Opportunity',
        description: '',
        classificationHints:
            "Keywords: checking in, still interested, follow up, toured recently, consider options, former family. Look for re-initiations of contact after a period of dormancy, particularly from 'toured-not-enrolled' or 'no-response' lifecycle statuses.\n\nMessages from past leads or dormant families showing renewed interest, or leads who have completed a tour but not yet enrolled.",
        responseHint: '',
        color: CATEGORY_COLORS.amber,
        active: true,
        sortOrder: 5,
    },
    {
        id: randomString(10),
        displayName: 'Enrollment Readiness',
        description: '',
        classificationHints:
            'Keywords: enroll, sign up, ready to start, paperwork, registration, tuition deposit. Look for phrases indicating a decision to move forward.\n\nMessages from parents who are ready to enroll their child or have specific questions about the enrollment process after expressing strong interest.',
        responseHint: '',
        color: CATEGORY_COLORS.emerald,
        active: true,
        sortOrder: 6,
    },
    {
        id: randomString(10),
        displayName: 'Positive Feedback/Referrals',
        description: '',
        classificationHints:
            'Keywords: happy, love, great, wonderful, recommend, refer, satisfied, thank you. Focus on positive sentiment and explicit referrals.\n\nMessages sharing positive experiences or offering to refer new families to TLE.',
        responseHint: '',
        color: CATEGORY_COLORS.emerald,
        active: true,
        sortOrder: 7,
    },
    {
        id: randomString(10),
        displayName: 'Schedule Change/Absence',
        description: '',
        classificationHints:
            "Keywords: absent, sick, won't be in, pick up early, drop off late, change schedule, vacation. Focus on notifications about attendance modifications.\n\nMessages from current parents regarding changes to their child's schedule, planned absences, or unexpected delays.",
        responseHint: '',
        color: CATEGORY_COLORS.slate,
        active: true,
        sortOrder: 8,
    },
    {
        id: randomString(10),
        displayName: 'Existing Parent Query',
        description: '',
        classificationHints:
            "Keywords: what did my child do, daily report, menu, upcoming events, portal, app. Excludes service issues or schedule changes.\n\nMessages from currently enrolled parents asking general questions about their child's day, activities, or minor administrative details.",
        responseHint: '',
        color: CATEGORY_COLORS.violet,
        active: true,
        sortOrder: 9,
    },
    {
        id: randomString(10),
        displayName: 'General',
        description: '',
        classificationHints:
            'Does not fit other categories. May be existing customers with routine questions, informational inquiries, or conversations that need more context to classify.\n\nOther conversations',
        responseHint: '',
        color: CATEGORY_COLORS.slate,
        active: true,
        sortOrder: 10,
    },
];

export type IntelligentThread = Thread & {
    suggestedReply?: string;
};

export const SAMPLE_THREADS: IntelligentThread[] = [
    {
        team_id: 1,
        contact_id: 1001,
        contact_name: 'Sarah Johnson',
        contact_phone: '+15551001001',
        last_message_content: 'Hi, I would love to schedule a tour for my 3 year old...',
        last_message_at: new Date().toISOString(),
        status: 'open',
        assignee_id: null,
        unread: 1,
        suggestedReply:
            "Hi Sarah! We'd love to show you around. We have tour slots available this week on Wednesday at 10 AM and Thursday at 2 PM. Would either of those work for you?",
    },
    {
        team_id: 1,
        contact_id: 1002,
        contact_name: 'Michael Chen',
        contact_phone: '+15551002002',
        last_message_content: 'Do you have any openings for the pre-K program?',
        last_message_at: new Date(Date.now() - 86400000).toISOString(),
        status: 'open',
        assignee_id: null,
        unread: 0,
        suggestedReply:
            "Hi Michael! Yes, we currently have a few spots open in our pre-K program. I'd be happy to share more details about the curriculum and schedule.",
    },
    {
        team_id: 1,
        contact_id: 1003,
        contact_name: 'Emily Rodriguez',
        contact_phone: '+15551003003',
        last_message_content: 'What are your tuition rates for the infant room?',
        last_message_at: '2025-02-19T10:00:00Z',
        status: 'open',
        assignee_id: null,
        unread: 0,
        suggestedReply:
            'Hi Emily! Our infant room tuition starts at $1,200/month for full-time care. We also offer flexible part-time options. Would you like to schedule a visit?',
    },
];

export const useIntelligentInbox = createGlobalState(() => {
    const categories = ref<IntelligentCategory[]>(createDefaultCategories());

    const sections = computed<IntelligentSection[]>(() => {
        const activeCategories = categories.value.filter((c) => c.active);
        return SECTION_DEFINITIONS.map((def) => ({
            state: def.state,
            label: def.label,
            color: def.color,
            categories: activeCategories,
        }));
    });

    const expandedSections = ref<Record<string, boolean>>(
        Object.fromEntries(SECTION_DEFINITIONS.map((def) => [def.state, true]))
    );

    const highlightedSection = ref<string | null>(null);

    const focusSection = (state: string): void => {
        for (const key of Object.keys(expandedSections.value)) {
            expandedSections.value[key] = false;
        }
        expandedSections.value[state] = true;
        highlightedSection.value = state;
        setTimeout(() => {
            highlightedSection.value = null;
        }, 1200);
    };

    return {
        categories,
        sections,
        expandedSections,
        highlightedSection,
        focusSection,
    };
});
